import * as React from 'react'

import { upsertLink } from '@platejs/link'
import { useEditorRef, useEditorSelector } from 'platejs/react'
import {
  RichTextField,
  BoldToolbarButton,
  ItalicToolbarButton,
  UnderlineToolbarButton,
} from '@buffer-mono/popcorn/rich-text'

import {
  Button,
  Flex,
  Toolbar,
  HeadingIcon,
  ImagePlusIcon,
  VideoIcon,
  LinkIcon,
  Dialog,
  Input,
  Label,
} from '@buffer-mono/popcorn'

import styles from './EditorToolbar.module.css'

import type { JSX } from 'react'

interface EditorToolbarProps {
  onInsertImage?: () => void
  onInsertEmbed?: () => void
}

export function EditorToolbar({
  onInsertImage,
  onInsertEmbed,
}: EditorToolbarProps): JSX.Element {
  const editor = useEditorRef()

  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false)
  const [linkUrl, setLinkUrl] = React.useState('')

  const isH2 = useEditorSelector(
    (currentEditor) => currentEditor.api.some({ match: { type: 'h2' } }),
    [],
  )
  const isLink = useEditorSelector(
    (currentEditor) => currentEditor.api.some({ match: { type: 'a' } }),
    [],
  )

  const handleHeading = (): void => {
    editor.tf.setNodes({ type: isH2 ? 'p' : 'h2' })
    editor.tf.focus()
  }

  const handleLinkClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    if (isLink) {
      editor.tf.unwrapNodes({ match: { type: 'a' } })
      editor.tf.focus()
    } else {
      setLinkUrl('')
      setLinkDialogOpen(true)
    }
  }

  const handleLinkSubmit = (): void => {
    if (linkUrl.trim()) {
      let url = linkUrl.trim()
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }
      try {
        new URL(url).toString()
      } catch {
        setLinkDialogOpen(false)
        setLinkUrl('')
        editor.tf.focus()
        return
      }
      upsertLink(editor, { url })
    }
    setLinkDialogOpen(false)
    setLinkUrl('')
    editor.tf.focus()
  }

  const handleInsertImage = (e: React.MouseEvent): void => {
    e.preventDefault()
    onInsertImage?.()
  }

  const handleInsertEmbed = (e: React.MouseEvent): void => {
    e.preventDefault()
    onInsertEmbed?.()
  }

  return (
    <>
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <Dialog.Content size="small">
          <Dialog.Header>
            <Dialog.Title>Insert Link</Dialog.Title>
            <Dialog.Description>Enter the URL for the link.</Dialog.Description>
          </Dialog.Header>
          <Dialog.Body>
            <Flex direction="column" gap="2xs">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className={styles.fullWidthInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleLinkSubmit()
                  }
                }}
              />
            </Flex>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Close>
              <Button variant="tertiary">Cancel</Button>
            </Dialog.Close>
            <Button variant="primary" onClick={handleLinkSubmit}>
              Insert
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <Toolbar aria-label="Content formatting" className={styles.toolbar}>
        <BoldToolbarButton />
        <ItalicToolbarButton />
        <UnderlineToolbarButton />
        <Toolbar.Separator />
        <RichTextField.ToolbarButton
          onClick={handleHeading}
          label="Toggle Heading (H2)"
          aria-pressed={isH2}
        >
          <HeadingIcon />
        </RichTextField.ToolbarButton>
        <RichTextField.ToolbarButton
          onClick={handleLinkClick}
          label={isLink ? 'Remove Link' : 'Insert Link'}
          aria-pressed={isLink}
        >
          <LinkIcon />
        </RichTextField.ToolbarButton>
        <Toolbar.Separator />
        <RichTextField.ToolbarButton
          onClick={handleInsertImage}
          label="Insert Image"
        >
          <ImagePlusIcon />
        </RichTextField.ToolbarButton>
        <RichTextField.ToolbarButton
          onClick={handleInsertEmbed}
          label="Insert Video"
        >
          <VideoIcon />
        </RichTextField.ToolbarButton>
      </Toolbar>
    </>
  )
}
